import { apiClient } from '../utils/apiClient';
import { useAuthStore } from '../store/authStore';

/**
 * The vault's device list (Standalone vault card X1) — desktop side.
 *
 *   registerThisDevice   once per app start after vault sync is on: "this
 *                        computer uses the cloud vault" (POST /me/vault/devices)
 *   listDevices          every device: desktop, extension, Android, web
 *   revokeDevice         stops that device getting the vault from the cloud;
 *                        a sign-in older than 10 minutes opens the browser
 *                        sign-in once and retries (like "Delete my cloud copy")
 *
 * A revoke cannot wipe what the device already downloaded — only changing the
 * master passphrase does (the UI says so). The server keys "this device" by
 * the sign-in's session id; the stable key here only tells a returning device
 * from a new one.
 */
const KEY = 'apiboo.vault.deviceKey';
// The sign-in (IdP session id) this computer last registered with: a new
// sign-in — signing out and in, or the re-auth before a Remove — is a new
// session, and the row must follow it or it points at a dead one.
let registeredSid;

/** The `sid` claim of the current access token (no verification — the server does that). */
function currentSid() {
  try {
    const token = localStorage.getItem('authToken') || '';
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json).sid || null;
  } catch {
    return null;
  }
}

function randomId() {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
}

function deviceKey() {
  try {
    let key = localStorage.getItem(KEY);
    if (!key) {
      key = `desktop-${randomId()}`;
      localStorage.setItem(KEY, key);
    }
    return key;
  } catch {
    return null;
  }
}

function deviceLabel() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/Windows/i.test(ua)) return 'Apiboo on Windows';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'Apiboo on macOS';
  if (/Linux/i.test(ua)) return 'Apiboo on Linux';
  return 'Apiboo desktop';
}

const errorOf = (e) => e?.response?.data?.error || '';

/** Registers this computer (once per sign-in). Never throws; 'device_revoked' when refused. */
export async function registerThisDevice({ force = false } = {}) {
  const sid = currentSid();
  if (registeredSid !== undefined && registeredSid === sid && !force) return 'ok';
  const key = deviceKey();
  if (!key) return 'skipped';
  try {
    await apiClient.post('/me/vault/devices', { device_key: key, label: deviceLabel() });
    registeredSid = sid;
    return 'ok';
  } catch (e) {
    return errorOf(e) === 'device_revoked' ? 'device_revoked' : 'failed';
  }
}

export async function listDevices() {
  return (await apiClient.get('/me/vault/devices')).data?.devices || [];
}

export async function revokeDevice(id, { reauth = useAuthStore.getState().loginWithBrowser } = {}) {
  const attempt = async () => (await apiClient.delete(`/me/vault/devices/${encodeURIComponent(id)}`)).data;
  try {
    return await attempt();
  } catch (e) {
    if (errorOf(e) !== 'recent_auth_required') throw e;
    const before = useAuthStore.getState().user?.id;
    const result = await reauth();
    if (!result?.success) throw new Error('reauth_cancelled');
    if (before && useAuthStore.getState().user?.id !== before) throw new Error('account_changed');
    // That sign-in is a new session for THIS computer too.
    await registerThisDevice({ force: true });
    return attempt();
  }
}

/** Tests only. */
export function __resetDeviceRegistration() {
  registeredSid = undefined;
}
